import { useCallback, useEffect, useState } from "react";
import { Landmark, Plus, X, ExternalLink } from "lucide-react";
import { authFetch } from "../utils/authFetch";
import { Button } from "./ui/button";

/**
 * Studio › Settings › Programs — Joveo-admin management of programs (rare,
 * deliberate events: creating a program, defining its admins). Day-to-day
 * program operation (partners, people, customers) lives in the Enable console.
 */

interface Program {
  id: string; slug: string; name: string; isActive: boolean;
  partners: number; adminCount: number;
  products: { slug: string; name: string }[];
  admins: { userId: string | null; inviteId?: string; email: string; name: string | null; pending: boolean }[];
}

export default function ProgramsSettings() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    authFetch("/api/admin/enable/programs").then((r) => (r.ok ? r.json() : []))
      .then((d) => { setPrograms(d); setLoaded(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = filter.trim().toLowerCase();
  const shown = q ? programs.filter((p) => p.name.toLowerCase().includes(q)) : programs;

  return (
    <div className="max-w-3xl">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="size-5 text-indigo-500" /> Programs
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            A program groups the partner firms implementing under one white-labeller (like SAP).
            Its admins get an Enable console over every partner and customer inside it — and
            nothing outside it. Partners inside a program are created from that console, not here.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus className="size-4" /> New program</Button>
      </div>

      {programs.length > 5 && (
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search programs…"
          className="mt-4 h-9 w-72 px-3 text-[13px] rounded-md border border-slate-200 bg-white placeholder:text-slate-400 focus:outline-none focus:border-indigo-400" />
      )}

      {creating && <NewProgramCard onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}

      <div className="mt-5 space-y-4">
        {!loaded ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
            {q ? "No programs match." : "No programs yet."}
          </div>
        ) : shown.map((p) => <ProgramCard key={p.id} program={p} onChanged={load} />)}
      </div>
    </div>
  );
}

function ProgramCard({ program, onChanged }: { program: Program; onChanged: () => void }) {
  const [email, setEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function grant() {
    setErr(null); setNotice(null);
    const r = await authFetch(`/api/admin/enable/programs/${program.slug}/admins`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim() }),
    });
    if (r.ok) {
      const d = await r.json();
      if (d.pending) setNotice(`Invite sent to ${d.email} — they become a program admin when they accept.`);
      setEmail(""); setAddingAdmin(false); onChanged();
    } else {
      const d = await r.json().catch(() => ({}));
      setErr(typeof d.detail === "string" ? d.detail : (d.detail?.message ?? "Couldn’t grant admin"));
    }
  }

  async function revoke(a: Program["admins"][number]) {
    const label = a.name || a.email;
    if (!window.confirm(a.pending ? `Revoke the pending invite for ${label}?` : `Remove ${label} as a program admin?`)) return;
    const r = a.pending
      ? await authFetch(`/api/admin/enable/programs/${program.slug}/admin-invites/${a.inviteId}`, { method: "DELETE" })
      : await authFetch(`/api/admin/enable/programs/${program.slug}/admins/${a.userId}`, { method: "DELETE" });
    if (r.ok) onChanged();
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
            {program.name}
            <span className="text-[11px] font-normal text-slate-400">/{program.slug}</span>
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {program.products.map((x) => x.name).join(" · ") || "no products"} · {program.partners} partner{program.partners === 1 ? "" : "s"}
          </div>
        </div>
        <a href={`/enable/program/${program.slug}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-800 shrink-0">
          Open console <ExternalLink className="size-3.5" />
        </a>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-medium text-slate-500">Program admins</span>
          {program.admins.map((a) => (
            <span key={a.userId ?? a.inviteId}
              className={`inline-flex items-center gap-1 text-[12px] rounded-full pl-2.5 pr-1 py-0.5 ${
                a.pending ? "text-amber-800 bg-amber-50 border border-amber-100" : "text-slate-700 bg-slate-100"}`}>
              {a.name || a.email}
              {a.pending && <span className="text-amber-500">· invited</span>}
              <button onClick={() => revoke(a)} aria-label={`Remove ${a.email}`}
                className={`p-0.5 rounded-full hover:text-rose-600 ${a.pending ? "text-amber-300" : "text-slate-300"}`}>
                <X className="size-3" />
              </button>
            </span>
          ))}
          {program.admins.length === 0 && <span className="text-[12px] text-slate-400">none yet</span>}
          {addingAdmin ? (
            <span className="inline-flex items-center gap-1.5">
              <input autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && grant()}
                placeholder="email@company.com"
                className="h-7 w-56 px-2 text-[12px] rounded-md border border-slate-200 bg-white focus:outline-none focus:border-indigo-400" />
              <Button size="sm" onClick={grant} disabled={!email.trim()}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingAdmin(false); setErr(null); }}>Cancel</Button>
            </span>
          ) : (
            <button onClick={() => setAddingAdmin(true)}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-800">
              <Plus className="size-3.5" /> Add
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Admins see every partner and customer in the program, invite people, assign work, and move
          requests. New emails get an Enable invite and become admins when they accept.
        </p>
        {notice && <p className="text-[11px] text-emerald-700 mt-1">{notice}</p>}
        {err && <p className="text-[11px] text-rose-600 mt-1">{err}</p>}
      </div>
    </div>
  );
}

function NewProgramCard({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [products, setProducts] = useState<{ slug: string; name: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    authFetch("/api/admin/enable/programs/products").then((r) => (r.ok ? r.json() : [])).then(setProducts);
  }, []);

  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  async function create() {
    setBusy(true); setErr(null);
    const r = await authFetch("/api/admin/enable/programs", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, name: name.trim(), product_slugs: [...selected] }),
    });
    setBusy(false);
    if (r.ok) onDone();
    else {
      const d = await r.json().catch(() => ({}));
      setErr(typeof d.detail === "string" ? d.detail : "Couldn’t create the program");
    }
  }

  return (
    <div className="mt-5 rounded-xl border border-indigo-200 bg-indigo-50/30 p-4">
      <div className="text-[14px] font-semibold text-slate-900 mb-3">New program</div>
      <div className="space-y-3 max-w-md">
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Name</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SAP Program"
            className="w-full h-9 px-3 text-[13px] rounded-md border border-slate-200 bg-white focus:outline-none focus:border-indigo-400" />
          {slug && <span className="block text-[11px] text-slate-400 mt-1">console at /enable/program/{slug}</span>}
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Products — partners added to the program are entitled to these</label>
          <div className="space-y-1">
            {products.map((p) => (
              <label key={p.slug} className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
                <input type="checkbox" checked={selected.has(p.slug)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(p.slug); else next.delete(p.slug);
                    setSelected(next);
                  }}
                  className="size-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                {p.name}
              </label>
            ))}
          </div>
        </div>
        {err && <p className="text-xs text-rose-600">{err}</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={create} disabled={busy || !slug || selected.size === 0}>Create program</Button>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
